// src/pages/About.jsx
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { PublicAPI } from '../api/public';
import { formatTextWithParagraphs } from '../utils/textHelpers';
import ChairmanSection from '../components/ChairmanSection';

const Container = styled.div`
  .container { 
    width: min(1400px, 96vw); 
    margin: 0 auto; 
    padding: 0 18px; 
  }
`;

const Content = styled.div`
  padding: 40px 0;
  
  .intro {
    font-size: 20px;
    line-height: 1.6;
    color: var(--muted);
    margin-bottom: 50px;
    text-align: center;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }
  
  .sections {
    display: grid;
    gap: 40px;
    margin: 50px 0;
  }
  
  .section {
    background: var(--card);
    border: 1px solid var(--bdr);
    border-radius: var(--radius);
    padding: 40px;
    box-shadow: var(--shadow);
    
    h2 {
      color: var(--primary);
      margin-bottom: 20px;
      font-size: 28px;
    }
    
    h3 {
      color: var(--fg);
      margin: 25px 0 15px 0;
      font-size: 22px;
    }
    
    p {
      color: var(--muted);
      line-height: 1.7;
      margin-bottom: 20px;
      font-size: 16px;
    }
    
    ul {
      list-style: none;
      padding: 0;
      margin: 20px 0;
      
      li {
        padding: 12px 0;
        color: var(--fg);
        position: relative;
        padding-left: 25px;
        line-height: 1.6;
        
        &:before {
          content: '▸';
          position: absolute;
          left: 0;
          color: var(--primary);
          font-weight: bold;
          font-size: 18px;
        }
      }
    }
    
    .highlight {
      background: rgba(176, 141, 87, 0.1);
      border-left: 4px solid var(--primary);
      padding: 20px;
      margin: 25px 0;
      border-radius: 8px;
      
      p {
        margin: 0;
        font-style: italic;
        color: var(--fg);
      }
    }
    
    .rich-content {
      h3 {
        color: var(--fg);
        margin: 25px 0 15px 0;
        font-size: 22px;
      }
      
      p {
        color: var(--muted);
        line-height: 1.7;
        margin-bottom: 20px;
        font-size: 16px;
      }
    }
  }
  
  .chairman-section {
    margin: 50px 0;
  }
  
  @media (max-width: 768px) {
    padding: 20px 0;
    
    .section {
      padding: 25px 20px;
      
      h2 {
        font-size: 24px;
      }
      
      h3 {
        font-size: 20px;
      }
    }
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: var(--muted);
`;

export default function About() {
  const { t, i18n } = useTranslation();
  const [content, setContent] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const currentLang = i18n.language || 'ru';
  const isRu = currentLang === 'ru';

  React.useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        const data = await PublicAPI.getCorporationContent();
        setContent(data);
      } catch (e) {
        console.error('Failed to load corporation content:', e);
        setError(e.message);
        // Используем дефолтные данные при ошибке
        setContent(getDefaultContent());
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, []);

  const getDefaultContent = () => ({
    title_ru: "О компании АСТОРИУС",
    title_en: "About ASTORIUS",
    intro_ru: "Мы — ведущая корпорация в сфере премиальной недвижимости, объединяющая экспертизу, инновации и безупречный сервис для создания исключительного опыта наших клиентов.",
    intro_en: "We are a leading corporation in the field of premium real estate, combining expertise, innovation and impeccable service to create an exceptional experience for our clients.",
    about_title_ru: "О корпорации",
    about_title_en: "About Corporation",
    about_content_ru: "<p>АСТОРИУС — это больше чем агентство недвижимости. Мы создаем будущее наших клиентов, предлагая не просто объекты, а инвестиционные возможности и жизненные решения.</p>",
    about_content_en: "<p>ASTORIUS is more than a real estate agency. We create the future of our clients by offering not just properties, but investment opportunities and life solutions.</p>",
  });

  const renderList = (listText) => {
    if (!listText) return null;
    const items = listText.split('\n').filter(item => item.trim());
    return (
      <ul>
        {items.map((item, index) => (
          <li key={index}>{item.trim()}</li>
        ))}
      </ul>
    );
  };

  const renderRichContent = (htmlContent) => {
    if (!htmlContent) return null;
    return (
      <div 
        className="rich-content"
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  };

  const renderTextContent = (textContent) => {
    if (!textContent) return null;
    return (
      <div dangerouslySetInnerHTML={{ __html: formatTextWithParagraphs(textContent) }} />
    );
  };

  if (loading) {
    return (
      <Container>
        <div className="container">
          <LoadingWrapper>
            Загрузка...
          </LoadingWrapper>
        </div>
      </Container>
    );
  }

  if (error && !content) {
    return (
      <Container>
        <div className="container">
          <LoadingWrapper>
            Ошибка загрузки контента
          </LoadingWrapper>
        </div>
      </Container>
    );
  }

  const data = content || getDefaultContent();

  return (
    <Container>
      <div className="container">
        <Content>
          <div className="intro">
            {isRu ? data.intro_ru : data.intro_en}
          </div>

          <div className="sections">
            {/* О корпорации */}
            {(data.about_title_ru || data.about_title_en) && (
              <div className="section">
                <h2>{isRu ? data.about_title_ru : data.about_title_en}</h2>
                {data.about_content_ru && (
                  isRu ? renderRichContent(data.about_content_ru) : renderRichContent(data.about_content_en)
                )}
                
                {(data.mission_title_ru || data.mission_title_en) && (
                  <>
                    <h3>{isRu ? data.mission_title_ru : data.mission_title_en}</h3>
                    {renderTextContent(isRu ? data.mission_content_ru : data.mission_content_en)}
                  </>
                )}
                
                {(data.quote_ru || data.quote_en) && (
                  <div className="highlight">
                    <p>"{isRu ? data.quote_ru : data.quote_en}"</p>
                  </div>
                )}
              </div>
            )}

            {/* Корпоративные ценности */}
            {(data.values_list_ru || data.values_list_en) && (
              <div className="section">
                <h2>{isRu ? data.values_title_ru : data.values_title_en}</h2>
                {renderList(isRu ? data.values_list_ru : data.values_list_en)}
              </div>
            )}

            {/* Направления деятельности */}
            {(data.directions_content_ru || data.directions_content_en) && (
              <div className="section">
                <h2>{isRu ? data.directions_title_ru : data.directions_title_en}</h2>
                {isRu ? renderRichContent(data.directions_content_ru) : renderRichContent(data.directions_content_en)}
              </div>
            )}

            {/* Корпоративная структура */}
            {(data.structure_content_ru || data.structure_content_en) && (
              <div className="section">
                <h2>{isRu ? data.structure_title_ru : data.structure_title_en}</h2>
                {renderTextContent(isRu ? data.structure_content_ru : data.structure_content_en)}
                {renderList(isRu ? data.structure_list_ru : data.structure_list_en)}
              </div>
            )}

            {/* Достижения */}
            {(data.achievements_content_ru || data.achievements_content_en) && (
              <div className="section">
                <h2>{isRu ? data.achievements_title_ru : data.achievements_title_en}</h2>
                {renderTextContent(isRu ? data.achievements_content_ru : data.achievements_content_en)}
                
                {(data.achievements_highlight_ru || data.achievements_highlight_en) && (
                  <div className="highlight">
                    <p>{isRu ? data.achievements_highlight_ru : data.achievements_highlight_en}</p>
                  </div>
                )}
                
                {renderList(isRu ? data.achievements_list_ru : data.achievements_list_en)}
              </div>
            )}
          </div>

          {/* Секция с обращениями председателя и президента */}
          <div className="chairman-section">
            <ChairmanSection />
          </div>
        </Content>
      </div>
    </Container>
  );
}
